import { message } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { RautFormDataState } from 'pages/reporting/reports/raut/rautReportData'
import { useCallback } from 'react'

interface UseLockManagementProps {
  metadataEntryId: string
  formData: RautFormDataState
  updateFormData: (data: Partial<RautFormDataState>) => void
  refreshMetadataEntry: () => Promise<void>
}

interface UseLockManagementResult {
  isLocked: boolean
  startedSignoff: boolean
  lastLockTimestamp?: string
  handleStartSignoff: () => Promise<void>
  handleUnlock: () => Promise<void>
  canStartSignoff: boolean
  canSignAsReviewer: boolean
}

export const useLockManagement = ({
  metadataEntryId,
  formData,
  updateFormData,
  refreshMetadataEntry,
}: UseLockManagementProps): UseLockManagementResult => {
  const { rolodexPatch } = useRolodexEdit()

  const handleStartSignoff = useCallback(async (): Promise<void> => {
    if (!metadataEntryId) {
      message.error('No metadata entry found')
      return
    }

    try {
      const lockState = {
        isLocked: true,
        startedSignoff: true,
        lastLockTimestamp: new Date().toISOString(),
      }

      // Clear any existing signatures when locking
      const newData: Partial<RautFormDataState> = {
        lockState,
        lock_state: lockState,
        signoff: { lastRevision: formData.signoff?.lastRevision || 0 },
      }

      // Update Rolodex entry
      await rolodexPatch([
        {
          id: metadataEntryId,
          data: newData,
        },
      ])

      // Update local state
      await updateFormData(newData)
      await refreshMetadataEntry()
      message.success('Report locked for sign-off')
    } catch (error) {
      console.error('Failed to lock report:', error)
      message.error('Failed to lock report')
    }
  }, [
    metadataEntryId,
    formData.signoff?.lastRevision,
    rolodexPatch,
    updateFormData,
    refreshMetadataEntry,
  ])

  const handleUnlock = useCallback(async (): Promise<void> => {
    if (!metadataEntryId) {
      message.error('No metadata entry found')
      return
    }

    try {
      const newData: Partial<RautFormDataState> = {
        lockState: {
          isLocked: false,
          startedSignoff: false,
          lastLockTimestamp: undefined,
        },
        lock_state: {
          isLocked: false,
          startedSignoff: false,
          lastLockTimestamp: undefined,
        },
        signoff: {
          lastRevision: formData.signoff?.lastRevision || 0,
          supervisorSignoff: null,
          reviewerSignoff: null,
          dataAnalystSignoff: null,
        },
      }

      // Update Rolodex entry
      await rolodexPatch([
        {
          id: metadataEntryId,
          data: newData,
        },
      ])

      // Update local state
      await updateFormData(newData)
      await refreshMetadataEntry()
      message.success('Report unlocked and signatures cleared')
    } catch (error) {
      console.error('Failed to unlock report:', error)
      message.error('Failed to unlock report')
    }
  }, [
    metadataEntryId,
    formData.signoff?.lastRevision,
    rolodexPatch,
    updateFormData,
    refreshMetadataEntry,
  ])

  // Computed properties
  const isLocked = formData.lockState?.isLocked ?? false
  const startedSignoff = formData.lockState?.startedSignoff ?? false
  const lastLockTimestamp = formData.lockState?.lastLockTimestamp

  // Determine if signoff can be started
  const canStartSignoff = !isLocked && !formData.signoff?.reviewerSignoff

  // Can only sign as reviewer if supervisor has signed and reviewer hasn't
  const canSignAsReviewer = Boolean(
    formData.signoff?.supervisorSignoff && !formData.signoff?.reviewerSignoff
  )

  return {
    isLocked,
    startedSignoff,
    lastLockTimestamp,
    handleStartSignoff,
    handleUnlock,
    canStartSignoff,
    canSignAsReviewer,
  }
}
